import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText, Label, Row } from 'reactstrap'
import Flex from '../../../common/Flex'
import Loader from '../../../common/Loader'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import { getRewardsAdminErrorMessage, simulateRewardsProgram } from '../../../../helpers/api/rewardsAdmin'
import { formatNumber, formatPercent, formatUsd } from '../../../rewards/rewardsUtils'

// Volúmenes de referencia del diseño del programa (HO-TASK-277): cuota
// promedio de 40 USD ($400.000 procesados con 10.000 usuarios).
export const USER_PRESETS = [10000, 100000]
export const DEFAULT_SIMULATION = { users: USER_PRESETS[0], averageFeeUsd: 40 }

const ResultTile = ({ label, value, tone }) => (
  <Col xs={6} md={4} xl={3} className="mb-3">
    <h6 className="text-600 fs--1 mb-1">{label}</h6>
    <p className={`fs-1 font-weight-semi-bold mb-0 text-break ${tone || 'text-900'}`}>{value}</p>
  </Col>
)

const SimulationResults = ({ result }) => {
  const negative = Number(result.net_margin_usd) <= 0
  return (
    <>
      <Row className="mt-2">
        <ResultTile label="Recaudación mensual" value={formatUsd(result.collection_usd)} />
        <ResultTile label="Ingreso por comisión" value={formatUsd(result.gateway_revenue_usd)} />
        <ResultTile label="Fondo de Puntos" value={formatUsd(result.points_fund_usd)} />
        <ResultTile label="Fondo de Premios" value={formatUsd(result.prize_fund_usd)} />
        <ResultTile
          label="Margen neto"
          value={`${formatUsd(result.net_margin_usd)} (${formatPercent(result.net_margin_percent)})`}
          tone={negative ? 'text-danger' : 'text-success'}
        />
        <ResultTile label="Comisión por usuario" value={formatUsd(result.commission_per_user_usd)} />
        <ResultTile label="Coins emitidas al mes" value={`${formatNumber(result.hc_issued)} HC`} />
        <ResultTile label="Pasivo que generan" value={formatUsd(result.hc_liability_usd)} />
      </Row>
      <Flex align="center" className="flex-wrap">
        <Badge color={result.liability_covered_by_points_fund ? 'soft-success' : 'soft-warning'} pill className="fs--2 mr-2 mb-1">
          {result.liability_covered_by_points_fund
            ? 'El aporte al Fondo de Puntos cubre las Coins del mes'
            : 'El aporte al Fondo de Puntos no cubre las Coins del mes'}
        </Badge>
        <span className="fs--2 text-500 mb-1">
          Los aportes suman el {formatPercent(Number(result.shares_total) * 100, 2)} de la comisión.
        </span>
      </Flex>
    </>
  )
}

// HO-TASK-289: proyección de costo y margen según el volumen de usuarios, con
// la configuración vigente (POST /rewards/config/simulate). No guarda nada.
const ProgramSimulator = ({ simulation, onChange }) => {
  const [draft, setDraft] = useState({ users: String(simulation.users), averageFeeUsd: String(simulation.averageFeeUsd) })
  const users = parseInt(draft.users, 10)
  const averageFeeUsd = Number(String(draft.averageFeeUsd).replace(',', '.'))
  const invalid = !(users >= 1 && users <= 10000000) || !(averageFeeUsd >= 0.01)

  const { data, error, isLoading } = useSWRFetch(
    ['rewards-simulate', simulation.users, simulation.averageFeeUsd],
    (key, simulatedUsers, fee) => simulateRewardsProgram({ users: simulatedUsers, averageFeeUsd: fee }),
    { revalidateOnFocus: false }
  )

  const apply = (next) => {
    setDraft({ users: String(next.users), averageFeeUsd: String(next.averageFeeUsd) })
    onChange(next)
  }

  const submit = (event) => {
    event.preventDefault()
    if (!invalid) onChange({ users, averageFeeUsd })
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <h5 className="fs-0 mb-1">Simulador de proyección</h5>
        <p className="fs--1 text-600 mb-3">
          Costo del programa y margen estimado de Homelan con los parámetros vigentes. Este volumen también se usa para
          mostrar el impacto de cada cambio antes de guardarlo.
        </p>
        <form onSubmit={submit}>
          <Row form className="align-items-end">
            <Col sm={6} lg={4}>
              <FormGroup>
                <Label for="simulator-users" className="fs--1">Usuarios que pagan al mes</Label>
                <Input
                  id="simulator-users"
                  type="number"
                  bsSize="sm"
                  min={1}
                  max={10000000}
                  step={1}
                  value={draft.users}
                  onChange={({ target }) => setDraft((current) => ({ ...current, users: target.value }))}
                />
              </FormGroup>
            </Col>
            <Col sm={6} lg={3}>
              <FormGroup>
                <Label for="simulator-fee" className="fs--1">Cuota promedio</Label>
                <InputGroup size="sm">
                  <Input
                    id="simulator-fee"
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={draft.averageFeeUsd}
                    onChange={({ target }) => setDraft((current) => ({ ...current, averageFeeUsd: target.value }))}
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText>USD</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>
            </Col>
            <Col xs="auto">
              <FormGroup>
                <Button color="primary" size="sm" type="submit" disabled={invalid}>
                  Calcular
                </Button>
              </FormGroup>
            </Col>
            <Col xs="auto">
              <FormGroup>
                <ButtonGroup size="sm" aria-label="Volúmenes de referencia">
                  {USER_PRESETS.map((preset) => (
                    <Button
                      key={preset}
                      color={simulation.users === preset ? 'primary' : 'falcon-default'}
                      aria-pressed={simulation.users === preset}
                      onClick={() => apply({ users: preset, averageFeeUsd: simulation.averageFeeUsd })}
                    >
                      {formatNumber(preset)}
                    </Button>
                  ))}
                </ButtonGroup>
              </FormGroup>
            </Col>
          </Row>
        </form>
        {invalid && <p className="fs--1 text-danger mb-2">Ingresa entre 1 y 10.000.000 usuarios y una cuota mayor que 0.</p>}
        {isLoading && <Loader padding="py-4" />}
        {error && <Alert color="danger" className="fs--1 mb-0">{getRewardsAdminErrorMessage(error, 'No pudimos calcular la proyección.')}</Alert>}
        {data?.data && <SimulationResults result={data.data} />}
      </CardBody>
    </Card>
  )
}

ProgramSimulator.propTypes = {
  simulation: PropTypes.shape({ users: PropTypes.number, averageFeeUsd: PropTypes.number }).isRequired,
  onChange: PropTypes.func.isRequired
}

export default ProgramSimulator
